<template>
  <property-report-settings
    v-if="!propertyId || !loading"
    :report="report"
    @submit="submit"
    @update-property-details="updatePropertyDetails"
    @cancel="safelyNavigate"
  />
  <q-inner-loading v-else showing color="secondary" />
</template>

<script lang="ts">
import type { RentSourceSubscriptionAttributes } from 'common'
import { usePropertyReportStore } from '@/stores/propertyreport'
import PropertyReportSettings from '@/components/PropertyReports/PropertyReportSettings.vue'
import { computePropertyEdits, deconstructPropertyEdits, propertyDetailToSearch } from '@/utils'
import { useUserStore } from '@/stores/user'
import { useRentsourceStore } from '@/stores/rentsource'
import type { RouteLocation } from 'vue-router'

export default {
  name: 'PropertyReportNewView',
  components: {
    PropertyReportSettings
  },
  beforeRouteLeave(to, from, next) {
    if (this.hasUnsavedChanges) {
      this.showUnsavedChangesDialog().then((shouldLeave) => {
        if (shouldLeave) {
          next()
        } else {
          next(false)
        }
      })
    } else {
      next()
    }
  },
  beforeRouteUpdate(to, from, next) {
    if (this.hasUnsavedChanges) {
      this.showUnsavedChangesDialog().then((shouldLeave) => {
        if (shouldLeave) {
          next()
        } else {
          next(false)
        }
      })
    } else {
      next()
    }
  },
  props: {
    propertyId: {
      type: String as () => string | null,
      default: null
    }
  },
  emits: ['update-property-details'],
  data() {
    return {
      loading: false,
      report: {
        id: -1,
        name: '',
        subject: null,
        comparable_addresses: [],
        excluded_property_ids: [],
        emails: this.currentEmails(),
        schedule: ['Monday'],
        user_id: useUserStore().user!.sub,
        modified_on: new Date().toISOString(),
        created_on: new Date().toISOString(),
        deleted_on: null,
        last_run_on: null,
        public_id: null,
        is_organization_visible: false,
        tags: ['Active'],
        property_edits: [],
        view_preferences: []
      } as RentSourceSubscriptionAttributes,
      manualPropertyEdits: {} as Record<string, Record<string, string | number>>,
      hasUnsavedChanges: true
    }
  },
  computed: {
    user() {
      return this.$auth0.user.value
    },
    propertyEdits() {
      return computePropertyEdits(this.manualPropertyEdits)
    }
  },
  watch: {
    propertyId: {
      immediate: true,
      handler(newPropertyId) {
        this.loadPropertyDetails(newPropertyId)
      }
    }
  },
  methods: {
    loadPropertyDetails(propertyId: string | null) {
      if (propertyId !== null) {
        this.loading = true
        this.report.subject = null
        this.report.name = ''
        useRentsourceStore()
          .details(propertyId)
          .then((data) => {
            this.report.subject = propertyDetailToSearch(data)
            this.report.name = data.building_name || data.street_address
          })
          .finally(() => {
            this.loading = false
          })
      } else {
        this.loading = false
        this.report.subject = null
        this.report.name = ''
      }
    },
    currentEmails() {
      const email = this.$auth0.user.value?.email
      return email ? [email] : []
    },
    updatePropertyDetails(propertyId: string, key: string, value: string | number) {
      if (!this.manualPropertyEdits[propertyId]) {
        this.manualPropertyEdits[propertyId] = {}
      }
      this.manualPropertyEdits[propertyId][key] = value
      this.$emit('update-property-details', deconstructPropertyEdits(this.propertyEdits))
    },
    showUnsavedChangesDialog() {
      return new Promise((resolve) => {
        this.$q
          .dialog({
            title: 'Leaving Report Creation',
            message:
              'You are about to leave this page. Any changes you have made will be lost. Are you sure you want to leave?',
            cancel: {
              label: 'Stay',
              flat: true,
              noCaps: true
            },
            ok: {
              label: 'Leave Page',
              flat: true,
              noCaps: true
            },
            persistent: true
          })
          .onOk(() => {
            resolve(true)
          })
          .onCancel(() => {
            resolve(false)
          })
      })
    },
    submit(report: RentSourceSubscriptionAttributes) {
      usePropertyReportStore()
        .create(report)
        .then((report) => {
          this.hasUnsavedChanges = false
          if (report) {
            this.$tracker.createdReport(report.id, 'new', 'property')
            this.$router.push({ name: 'Property Report', params: { reportId: report.id } })
          } else {
            this.$router.push({ name: 'Dashboard' })
          }
        })
    },
    safelyNavigate(to: RouteLocation) {
      this.hasUnsavedChanges = false
      this.$router.push(to)
    }
  }
}
</script>
